/**
 * Locale-aware date formatters.
 *
 * Returns memoized format functions bound to the active locale + translated
 * labels. Behaviour mirrors Flutter `lib/presentation/utils/date_utils.dart`
 * (`getDate`).
 */

import { useMemo } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import type { AppLocale } from '@/domain/enums/appLocale';

const formatTimeOfDay = (date: Date, locale: AppLocale): string =>
  date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });

const formatDayMonth = (date: Date, locale: AppLocale, includeYear: boolean): string =>
  date.toLocaleDateString(
    locale,
    includeYear
      ? { day: '2-digit', month: 'short', year: 'numeric' }
      : { day: '2-digit', month: 'short' },
  );

export interface DateFormatters {
  /**
   * Mirrors Flutter `getDate`.
   * - Today: time of day only (no "Today" label, no separator word).
   * - Yesterday / Tomorrow: localized label, optionally "<label> HH:MM" when includeTime is true.
   * - Same year: "dd MMM" (optionally + time).
   * - Other year: "dd MMM yyyy" (optionally + time).
   */
  formatDate: (timestamp: number, includeTime?: boolean) => string;
  /**
   * Format a call duration (in milliseconds) into a friendly localized string.
   *
   * Ports Flutter's `DateUtils.getFriendlyLiveDuration`
   * (`lib/presentation/utils/date_utils.dart:167-197`) but localizes the unit
   * words via l10n and fixes two Flutter bugs (per `conventions.md`: obvious
   * Flutter bugs — typos and dead branches — are NOT ported):
   *  - `mimute` / `mimutes` typo → `minute` / `minutes`.
   *  - The inner pluralization check on the hours+minutes branch was
   *    `minutes == 1`, which is always false (the branch is only reached
   *    when `minutes >= 60`). Fixed to `hourMinutes == 1` so a duration like
   *    "1 hour and 1 minute" renders with the singular form.
   *
   * Rules:
   *  - seconds = ceil(ms / 1000); if seconds < 60 → `"{seconds} {seconds-label}"`.
   *  - minutes = ceil(seconds / 60); if minutes < 60:
   *      - 1 → `"1 {minute-label}"`; else → `"{minutes} {minutes-label}"`.
   *  - else:
   *      - hours = floor(minutes / 60); hourMinutes = minutes - hours * 60.
   *      - hours text uses singular/plural based on `hours === 1`.
   *      - minutes text uses singular/plural based on `hourMinutes === 1`.
   *      - returns `"{hoursText} {joiner} {hourMinutes} {minutesText}"`.
   */
  formatCallDuration: (milliseconds: number) => string;
}

export const useDateFormatters = (): DateFormatters => {
  const { locale, l10n } = useLocale();

  return useMemo<DateFormatters>(() => {
    const labels = l10n.common;
    const callDurationLabels = l10n.dateFormatters.callDuration;

    const formatDate = (timestamp: number, includeTime: boolean = false): string => {
      if (!timestamp) return '';

      const date = new Date(timestamp);
      const now = new Date();

      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
      const oneDayMs = 24 * 60 * 60 * 1000;

      const time = formatTimeOfDay(date, locale);
      if (startOfDate === startOfToday) return time;

      const withTime = (label: string) => (includeTime ? `${label}, ${time}` : label);
      if (startOfDate === startOfToday - oneDayMs) return withTime(labels.yesterday);
      if (startOfDate === startOfToday + oneDayMs) return withTime(labels.tomorrow);

      const sameYear = date.getFullYear() === now.getFullYear();
      return withTime(formatDayMonth(date, locale, !sameYear));
    };

    const formatCallDuration = (milliseconds: number): string => {
      const seconds = Math.ceil(milliseconds / 1000);
      if (seconds < 60) {
        return `${seconds} ${callDurationLabels.seconds}`;
      }

      const minutes = Math.ceil(seconds / 60);
      if (minutes < 60) {
        if (minutes === 1) {
          return `${minutes} ${callDurationLabels.minute}`;
        }
        return `${minutes} ${callDurationLabels.minutes}`;
      }

      const hours = Math.floor(minutes / 60);
      const hourMinutes = minutes - hours * 60;
      const hoursText =
        hours === 1
          ? `${hours} ${callDurationLabels.hour}`
          : `${hours} ${callDurationLabels.hours}`;
      const minutesText =
        hourMinutes === 1
          ? `${hourMinutes} ${callDurationLabels.minute}`
          : `${hourMinutes} ${callDurationLabels.minutes}`;

      return `${hoursText} ${callDurationLabels.joiner} ${minutesText}`;
    };

    return { formatDate, formatCallDuration };
  }, [locale, l10n]);
};
